import { api } from "../client";

export type CalendarEvent = {
  id: string;
  title: string;
  start: string;        // ISO-8601 UTC, e.g. 2026-05-21T09:00:00Z
  end: string;          // ISO-8601 UTC
  location: string;
  conference_url: string | null;
  conference_platform: string | null;   // "google_meet" | "teams" | "zoom" | "webex" | null
  attendees: { email: string; name: string }[];
  provider: "google" | "microsoft";
};

export type CalendarUpcoming = {
  connected: boolean;
  providers: string[];
  events: CalendarEvent[];
};

type ResponseEnvelope<T> = {
  success: boolean;
  message: string;
  data: T;
};

// A calendar the user can add events to (calendar.calendarlist.readonly).
export type CalendarListItem = { id: string; name: string };

// Raw Aurinko event shape (the ranged /events endpoint returns this unwrapped —
// no server-side normalizer on that path, unlike /v1/calendar/upcoming).
type RawAurinkoEvent = {
  id: string;
  subject?: string;
  start?: { dateTime?: string; timezone?: string } | null;
  end?: { dateTime?: string; timezone?: string } | null;
  location?: string | { displayName?: string } | null;
  organizer?: { emailAddress?: { address?: string; name?: string } } | null;
  meetingInfo?: {
    url?: string | null;
    attendees?: Array<{ address?: string; name?: string; emailAddress?: { address?: string; name?: string } }>;
  } | null;
  onlineMeetingUrl?: string | null;
  htmlLink?: string | null;
};

function normalizeAurinkoEvent(e: RawAurinkoEvent): CalendarEvent {
  const attendees = (e.meetingInfo?.attendees ?? []).map((a) => {
    const email = a.address ?? a.emailAddress?.address ?? "";
    return { email, name: a.name ?? a.emailAddress?.name ?? email };
  });
  const location = typeof e.location === "string" ? e.location : e.location?.displayName ?? "";
  return {
    id: e.id,
    title: e.subject ?? "(no title)",
    start: e.start?.dateTime ?? "",
    end: e.end?.dateTime ?? "",
    location,
    conference_url: e.meetingInfo?.url ?? e.onlineMeetingUrl ?? null,
    conference_platform: null,
    attendees,
    provider: "google",
  };
}

export const calendarApi = {
  listUpcoming: async (windowHours = 168, limit = 50): Promise<CalendarUpcoming> => {
    const res = await api.get<ResponseEnvelope<CalendarUpcoming>>(
      `/v1/calendar/upcoming?window_hours=${windowHours}&limit=${limit}`,
    );
    return res.data;
  },

  // List the user's calendars to populate the "Add to calendar" picker.
  // Served by ubik-api via Aurinko (calendar.calendarlist.readonly).
  listCalendars: async (): Promise<CalendarListItem[]> => {
    const res = await api.get<{ success: boolean; data: Array<Record<string, unknown>> }>(
      "/v1/aurinko/calendar/calendars",
    );
    return (res.data ?? []).map((c) => ({
      id: String(c.id ?? c.calendarId ?? "primary"),
      name: String(c.name ?? c.summary ?? c.id ?? "Calendar"),
    }));
  },

  // Create an event on the chosen calendar (calendar.events). start/end are ISO strings.
  createEvent: async (input: {
    calendarId: string;
    subject: string;
    start: string;
    end: string;
    description?: string;
    location?: string;
    attendees?: string[];
    timeZone?: string;
  }): Promise<void> => {
    const tz = input.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    await api.post("/v1/aurinko/calendar/events", {
      calendarId: input.calendarId,
      subject: input.subject,
      start: { dateTime: input.start, timeZone: tz },
      end: { dateTime: input.end, timeZone: tz },
      description: input.description,
      location: input.location,
      attendees: input.attendees?.map((a) => ({ address: a })),
    });
  },

  // Events in a [timeMin, timeMax] window for the month grid. Backend returns
  // raw Aurinko records (calendar_id defaults to the user's primary calendar).
  listEvents: async (timeMin: string, timeMax: string, calendarId?: string): Promise<CalendarEvent[]> => {
    const qs = new URLSearchParams({ time_min: timeMin, time_max: timeMax, limit: "250" });
    if (calendarId) qs.set("calendar_id", calendarId);
    const res = await api.get<{ records?: RawAurinkoEvent[] } | RawAurinkoEvent[]>(
      `/v1/aurinko/calendar/events?${qs.toString()}`,
    );
    const records = Array.isArray(res) ? res : res?.records ?? [];
    return records.map(normalizeAurinkoEvent);
  },
};
